import type { DropdownOption } from "../../../components/ui";
import type { RevenuePeriodId } from "../types/revenue.types";

/**
 * The stretches of time the screen can be looked at over, and how many months
 * back each one reaches. `null` is "all time", which asks for no window at all.
 *
 * Months are walked on the calendar rather than counted as days: "last month"
 * means back to the same date of the month before — the 11th of August reaches
 * the 11th of July — which is how someone reading it means it, and how the
 * endpoint's `from` is written.
 */
const PERIOD_MONTHS: Record<RevenuePeriodId, number | null> = {
  "last-month": 1,
  "last-3-months": 3,
  "last-6-months": 6,
  "last-year": 12,
  all: null,
};

/** What the dropdown above the table offers, in the order it offers it. */
export const REVENUE_PERIODS: DropdownOption[] = [
  { value: "all", label: "All time" },
  { value: "last-month", label: "Last month" },
  { value: "last-3-months", label: "Last 3 months" },
  { value: "last-6-months", label: "Last 6 months" },
  { value: "last-year", label: "Last year" },
];

/**
 * The window the screen opens on: everything. A dietitian opening Revenue is
 * asking what the practice has taken, not what it took since a date the screen
 * picked for them — and the shorter windows are one press away.
 */
export const DEFAULT_REVENUE_PERIOD: RevenuePeriodId = "all";

/** Both ends of a window, as the endpoint's inclusive `from` and `to`. */
export interface RevenueRange {
  /** ISO date, `2026-07-11`. */
  from: string;
  to: string;
}

/** A day as `YYYY-MM-DD`, read off the local calendar. `toISOString` would
 *  shift it to UTC, which puts an evening in India on the following day. */
const isoDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

/**
 * The same date, `months` calendar months earlier.
 *
 * The shift is done from the first of the month so it cannot overflow — moving
 * the 31st back a month lands JavaScript on the 3rd of the month after the one
 * meant — and the day is then put back, or clamped to the last day where the
 * month it lands in is shorter: a window opened on the 31st of March reaches
 * back to the 28th of February, not into March.
 */
const monthsBefore = (date: Date, months: number): Date => {
  const moved = new Date(date);
  moved.setDate(1);
  moved.setMonth(moved.getMonth() - months);

  const lastDayOfMonth = new Date(
    moved.getFullYear(),
    moved.getMonth() + 1,
    0
  ).getDate();
  moved.setDate(Math.min(date.getDate(), lastDayOfMonth));
  return moved;
};

/**
 * The window a period asks the backend for — from that date to today, both
 * ends included — or `null` for "all time", where there is no window to ask
 * for and the endpoint answers with everything.
 *
 * `now` is passed rather than read here so a caller working out both ends of
 * one window gets them off the same clock.
 */
export const periodRange = (
  period: RevenuePeriodId,
  now: Date = new Date()
): RevenueRange | null => {
  const months = PERIOD_MONTHS[period];
  if (months === null) return null;

  return { from: isoDate(monthsBefore(now, months)), to: isoDate(now) };
};
